/* External */
import { memo, useCallback, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';

/* Ott-common */
import { Errors } from '@dtvgo/player-utils';

/* Components */

/* Hooks */
import { useOnBackground } from '~/providers/ForegroundAppProvider';

/* Utils */
import { isEpisode } from '~/utils/metaDataUtils';
import { PLAY_SOURCE_PAGE, PLAY_SOURCE_SECTION } from '~/utils/trackSourceConviva';
import { Routes, getLink } from '~/utils/router';
import { useOnKeyDown } from '~/utils/keyDownEvents';
import platform from '~/utils/platform';
import { goBack } from '~/utils/history';
import useQuery from '~/hooks/useQuery';
import useFetchVodContentProgress from '~/hooks/useFetchVodContentProgress';
import useNpawError from '~/hooks/useNpawError';
import useRedirectToPlayer from '~/hooks/useRedirectToPlayer';
import useNextEpisode from '~/hooks/useNextEpisode';
import useFetchContent from '~/hooks/useFetchContent';
import useParentalControlError from '~/components/Player/utils/useParentalControlError';
import useHandlePlayerErrorWithReloadOnConnected from '~/components/Player/utils/useHandlePlayerErrorWithReloadOnConnected';
import PlayerMessage from '~/components/Player/PlayerMessage';
import NpawMonitoringContainer from '~/components/Player/NpawMonitoring.container';
import { VrioPlayer } from '~/components/Player';

const requiredProps = [
  'streaming',
  'metadata',
  'genres',
  'vmsId',
];
const STOP_BUTTONS = [platform.keyCodes.mediaControl.stop];

function VodPlayer() {
  const { state: locationState } = useLocation();
  const history = useHistory();
  const [counterEpisode, setCounterEpisode] = useState(0);
  const [authorizerResponse, setAuthorizerResponse] = useState({});

  const { contentId } = useParams();
  const { origin, watchFromStart } = useQuery();

  const { content } = useFetchContent({
    contentId,
    requiredProps,
  });

  const { savedProgress } = useFetchVodContentProgress({
    contentId,
    duration: content?.duration,
    isEpisode: isEpisode(content),
  });

  const savedProgressToUse = watchFromStart ? 0 : savedProgress;

  const nextEpisode = useNextEpisode(content);

  const goToNextEpisode = useRedirectToPlayer(nextEpisode, {
    page: PLAY_SOURCE_PAGE.PLAYER,
    section: PLAY_SOURCE_SECTION.VOD_NEXT_EPISODE,
    replace: true,
    state: locationState,
  });

  const {
    error, handleError, reportableError, handleReportableError,
  } = useHandlePlayerErrorWithReloadOnConnected();

  const hasNextEpisode = isEpisode(content) && !!nextEpisode;

  const exitPlayer = useCallback(() => {
    goBack();
  }, []);

  // When the app goes to background, exits the player to avoid unexpected errors
  useOnBackground(exitPlayer);
  useOnKeyDown({ listener: exitPlayer, forKeyCodes: STOP_BUTTONS });

  const handleUserInteraction = useCallback(() => {
    setCounterEpisode(0);
  }, []);

  const handleEnded = useCallback(() => {
    if (hasNextEpisode) {
      goToNextEpisode();
      setCounterEpisode((counter) => counter + 1);
    } else {
      exitPlayer();
    }
  }, [exitPlayer, goToNextEpisode, hasNextEpisode]);

  const [readyToPlay, setReadyToPlay] = useState(false);

  const npawError = useNpawError(reportableError);

  useParentalControlError(content, {
    onError: handleReportableError,
    readyToPlay,
  });

  if (error) {
    // TODO: This should be handled by ErrorScreen, too
    if (error?.category === Errors.ErrorCategory.PARENTAL) {
      history.replace(
        getLink(Routes.Private.Player.Error),
        {
          parentalControl: true,
          content,
        },
      );
    } else {
      return (
        <PlayerMessage
          content={content}
          error={error}
          contentTitle={content?.episode?.showName || content?.title}
          exit={exitPlayer}
        />
      );
    }
  }

  return (
    <VrioPlayer
      channelId={authorizerResponse?.channelId}
      playbackId={contentId}
      content={content}
      hasNextEpisode={hasNextEpisode}
      playSource={origin}
      savedProgress={savedProgressToUse}
      onEnded={handleEnded}
      goToNextEpisode={hasNextEpisode ? goToNextEpisode : undefined}
      onUserInteraction={handleUserInteraction}
      counterEpisode={counterEpisode}
      onError={handleError}
      onAuthorizerError={handleReportableError}
      setPlayerReadyToPlay={setReadyToPlay}
      onAuthorizerResponseReceived={setAuthorizerResponse}
    >
      <NpawMonitoringContainer
        content={content}
        channelId={authorizerResponse.channelId}
        savedProgress={savedProgressToUse}
        playSource={origin}
        error={npawError}
        provider={authorizerResponse.provider}
      />
    </VrioPlayer>
  );
}

export default memo(VodPlayer);
